import { useEffect, useState } from "react";
import { api, type MeResponse } from "./api";

// Lightweight current-user hook. Real sessions come from SSOJet; in dev a header
// impersonates a user (see api.setDevUser).
export function useMe() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api
      .me()
      .then(setMe)
      .catch(() => setMe({ user: null, memberships: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);
  return { me, loading, refresh };
}

const isBrowser = typeof document !== "undefined";

/** Reads the theme the pre-paint inline script already applied. */
function initialTheme(): string {
  // The server has no document; it always renders the light default, matching
  // what the inline script sets before first paint on the client.
  if (!isBrowser) return "light";
  return document.documentElement.getAttribute("data-theme") ?? "light";
}

export function useTheme() {
  const [theme, setTheme] = useState<string>(initialTheme);
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    if (isBrowser) document.documentElement.setAttribute("data-theme", next);
    try {
      if (isBrowser) localStorage.setItem("cc-theme", next);
    } catch {
      // Private mode or blocked site data — the toggle still works for this page.
    }
    setTheme(next);
  };
  return { theme, toggle };
}
